/**
 * Qué concluyó el agente de cada archivo, dicho para el ejecutivo.
 *
 * POR QUÉ EXISTE (Leslie y Diego Casallo, TEST, 19-oct-2026)
 * ----------------------------------------------------------
 * «El ejecutivo no tiene una vista de las observaciones con los documentos, y
 * tampoco cuáles son los validados para descargarlos.» En el caso que
 * mostraron, el cliente leyó «No logramos leer correctamente el documento»
 * sobre `VAR_DNI-03_borroso.jpg` y la tarjeta decía «Por revisar · IA 1.0»;
 * el DNI que el agente rechazó tres veces decía «En Drive», en verde, igual
 * que la Copia Literal que sí aceptó. «En Drive» es dónde quedó guardado, no
 * un veredicto, y el «IA 88.0» es la confianza del CLASIFICADOR en el tipo.
 *
 * El backend manda `veredicto_agente` por archivo
 * (`teleagent/veredicto_documento.py`); acá se decide, una vez, cómo se lee en
 * la tarjeta y en qué grupo del filtro cae. La decisión de una PERSONA
 * (aprobar o rechazar) manda sobre la del agente para el grupo, y se dice
 * aparte: son dos decisiones.
 *
 * 20-oct-2026 (Diego Casallo): el ejecutivo valida o rechaza. Lo que la IA
 * validó sigue validado —Ruben: dejarlo «por validar» era un falso positivo—,
 * y lo que valida una persona dice quién lo validó.
 *
 * Puro y sin imports de paquetes: lo corre
 * `tests/unit/test_veredicto_por_documento.py` en Node con lo que devuelve el
 * portal de verdad.
 */
import { fechaHoraEnLima } from './documentos'

/** `teleagent.veredicto_documento`: los cruza un test, literal por literal. */
export const VALIDADO = 'VALIDADO'
export const CON_OBSERVACIONES = 'CON_OBSERVACIONES'
export const OBSERVADO = 'OBSERVADO'
export const NO_IDENTIFICADO = 'NO_IDENTIFICADO'
export const NO_REQUERIDO = 'NO_REQUERIDO'
export const EN_REVISION = 'EN_REVISION'
export const SIN_EVALUAR = 'SIN_EVALUAR'
export const SIN_VEREDICTO = 'SIN_VEREDICTO'

export type Tono = 'ok' | 'aviso' | 'mal' | 'info' | 'neutro'
export type Grupo = 'validados' | 'observados' | 'sin_validar'

/** Lo que este módulo lee de un documento: un subconjunto de `ConversationDocument`. */
export interface DocConVeredicto {
  status: string
  otro_inmueble?: string
  revisado_por?: string
  revisado_at?: string
  /** El motivo que eligió la persona al rechazar. */
  motivo_de_la_persona?: string
  veredicto_agente?: {
    resultado: string
    titulo?: string
    case_id?: string
    motivo?: string
    texto_cliente?: string
    requisitos?: string[]
    intento?: number
    en_silencio?: boolean
    registrado?: boolean
    observaciones?: string[]
    ts?: string
  } | null
}

export interface LecturaDelVeredicto {
  grupo: Grupo
  tono: Tono
  /** El chip, corto: «Validado por IA». */
  chip: string
  /** La primera línea del bloque: «Observado por el agente IA: DNI no corresponde…». */
  titulo: string
  /** «Cubre: Copia Literal del inmueble» / «Venía a cubrir: …», o ''. */
  requisitos: string
  /** Lo interno: de quién es el DNI, qué leyó el OCR. */
  motivo: string
  /** «Intento 3», o ''. */
  intento: string
  /** Lo que leyó el cliente por este archivo, o ''. */
  textoCliente: string
  /** Por qué no hay texto al cliente, cuando no lo hay y debería saberse. */
  nota: string
  /** Las alertas guardadas bajo el archivo. */
  observaciones: string[]
  /** «Validado por Diego Casallo · 19/10 17:40», o ''. */
  persona: string
}

const ACEPTADOS = new Set([VALIDADO, CON_OBSERVACIONES])
const OBJETADOS = new Set([OBSERVADO, NO_IDENTIFICADO])

/**
 * En qué grupo cae un archivo. La persona manda: lo que rechazó es observado
 * aunque la IA lo haya validado, y lo que validó es validado. Lo apartado por
 * otro inmueble no es del caso.
 */
export function grupoDelDocumento(doc: DocConVeredicto): Grupo {
  if (doc.status === 'REJECTED') return 'observados'
  if (doc.status === 'APPROVED') return 'validados'
  if (doc.otro_inmueble) return 'sin_validar'
  const resultado = doc.veredicto_agente?.resultado ?? ''
  if (ACEPTADOS.has(resultado)) return 'validados'
  if (OBJETADOS.has(resultado)) return 'observados'
  return 'sin_validar'
}

const CHIP: Record<string, { chip: string; tono: Tono }> = {
  [VALIDADO]:          { chip: 'Validado por IA',               tono: 'ok' },
  [CON_OBSERVACIONES]: { chip: 'Validado con observaciones',    tono: 'aviso' },
  [OBSERVADO]:         { chip: 'Observado por IA',              tono: 'mal' },
  [NO_IDENTIFICADO]:   { chip: 'No identificado',               tono: 'mal' },
  [NO_REQUERIDO]:      { chip: 'No lo pide el checklist',       tono: 'neutro' },
  [EN_REVISION]:       { chip: 'Lo decide una persona',         tono: 'info' },
  [SIN_EVALUAR]:       { chip: 'Sin evaluar',                   tono: 'neutro' },
  [SIN_VEREDICTO]:     { chip: 'Sin veredicto registrado',      tono: 'neutro' },
}

function primeraLinea(resultado: string, titulo: string): string {
  const causa = titulo ? `: ${titulo}` : ''
  switch (resultado) {
    case VALIDADO: return 'Validado por el agente IA'
    case CON_OBSERVACIONES: return `Validado por el agente IA, con observaciones${causa}`
    case OBSERVADO: return `Observado por el agente IA${causa}`
    case NO_IDENTIFICADO: return `El agente IA no pudo identificarlo${causa}`
    case NO_REQUERIDO: return `No cubre ningún requisito del checklist${causa}`
    case EN_REVISION: return `El agente IA no lo validó: lo decide una persona${causa}`
    case SIN_EVALUAR: return 'Sin evaluar: llegó antes de que hubiera checklist'
    default: return 'No quedó registrado qué decidió el agente IA con este archivo'
  }
}

/** Cómo se lee el veredicto en la tarjeta del documento. */
export function lecturaDelVeredicto(doc: DocConVeredicto): LecturaDelVeredicto {
  const v = doc.veredicto_agente ?? null
  const resultado = v?.resultado && CHIP[v.resultado] ? v.resultado : SIN_VEREDICTO
  const { chip, tono } = CHIP[resultado]
  const titulo = (v?.titulo || '').trim()
  const requisitos = (v?.requisitos ?? []).filter((r) => r)
  const textoCliente = (v?.texto_cliente || '').trim()

  let nota = ''
  if (!textoCliente && v?.en_silencio && resultado !== SIN_VEREDICTO) {
    nota = 'Se anotó con una persona atendiendo: al cliente no se le dijo nada.'
  } else if (v && v.registrado === false && resultado !== SIN_VEREDICTO) {
    nota = 'Reconstruido de lo guardado: el archivo llegó antes de que se registrara el veredicto.'
  }

  let persona = ''
  if ((doc.status === 'APPROVED' || doc.status === 'REJECTED')) {
    const quien = (doc.revisado_por || '').trim() || 'una persona'
    const cuando = fechaHoraEnLima(doc.revisado_at)
    const motivo = doc.status === 'REJECTED' ? (doc.motivo_de_la_persona || '').trim() : ''
    persona = `${doc.status === 'APPROVED' ? 'Validado' : 'Rechazado'} por ${quien}`
      + (cuando ? ` · ${cuando}` : '') + (motivo ? ` · ${motivo}` : '')
  }

  return {
    grupo: grupoDelDocumento(doc),
    tono,
    chip,
    titulo: primeraLinea(resultado, titulo),
    requisitos: requisitos.length === 0 ? ''
      : `${ACEPTADOS.has(resultado) ? 'Cubre' : 'Venía a cubrir'}: ${requisitos.join(', ')}`,
    motivo: (v?.motivo || '').trim(),
    intento: v?.intento ? `Intento ${v.intento}` : '',
    textoCliente,
    nota,
    observaciones: (v?.observaciones ?? []).filter((o) => o),
    persona,
  }
}
